<script setup lang="ts">
import Debug from 'debug'
import { useDialog } from 'primevue/usedialog'
import { storeToRefs } from 'pinia'
import { publicConfig } from './config/public'
import { RestaurantAreas } from './lib/interfaces/restaurant'
import { RestaurantInfo } from './lib/services/store/restaurant/restaurant.dto'
import { EnrollmentScreensData } from './lib/services/store/auth.dto'
import { ToastType } from './lib/models/ui/toast'
import { createGTMScript } from '~/utils/gtmUtils'
import { QueryParamsRating } from '~/lib/models/rating'
import { getRestaurantEcommerceHomeRoute } from '~/utils/restaurantUtils'

const debug = Debug('app:site')

const contentStore = useContentStore()
const appStore = useAppStore()
const authStore = useAuthStore()
const { initialFetchLoaded } = storeToRefs(appStore)
const domain = useSiteDomain()
const origin = useSiteDomain({ protocol: true, noOverride: true })
const route = useRoute()
const dialog = useDialog()

const nuxtApp = useNuxtApp()
const { locale, t } = useLocale()

const { path, fullPath } = route
const loading = ref<boolean>(false)
const errorView = ref()
const overriding = ref<boolean>(false)
const gtmContainerScript = ref<string>(createGTMScript(publicConfig.gtm.containerId))
const screens = ref<EnrollmentScreensData[]>([])

useHead({
  script: [{ innerHTML: gtmContainerScript.value, defer: true }],
})

// Fetch init on SSR
const { error: errorSite } = await useWrapperKeyAsyncData(
  'fetchSite',
  () => appStore.fetchSite(domain),
  {
    server: true,
  }
)
if (errorSite.value) {
  debug('Fetch error fetchSite', errorSite.value)
  errorView.value = errorSite.value
}

/*  Fetch initial other data request on client */
const { error } = await useLazyAsyncData('initialFetch', () => appStore.initialFetch(), {
  server: false,
})

const { $toast } = useNuxtApp()
const userHasRegistered = ref<boolean>(false)
const showData = ref<boolean>(false)
const { trackEnrollmentCompleted } = useGTM()

const closeShowData = () => {
  showData.value = false
}

const succededEnroll = () => {
  closeShowData()
  $toast.show({
    content: t('dataOnboarding.succeeded'),
    type: ToastType.SUCCESS,
  })
  trackEnrollmentCompleted()
}

// Set locale and previously loaded on appStore
locale.value = appStore.language

const sameasLinks = computed(() => {
  if (!contentStore.site || !contentStore.site?.footer?.footerSectionsSocial) return undefined
  const links = contentStore.site.footer.footerSectionsSocial.menuItemsSocial.reduce(
    (acc, curr) => {
      const urls = curr?.linkSocial.map(link => link?.url || undefined) || []
      urls.forEach(url => {
        if (url) acc.push(url)
      })
      return acc
    },
    [] as string[]
  )
  return links
})

const langHtml = computed(() => {
  const langISO = appStore.language.replace('_', '-')
  if (langISO === 'pt') return `${langISO}-br`
  return langISO
})

const readRatingParams = ({ path, fullPath }: { path: string; fullPath: string }) => {
  if (appStore.ratingIntentData) return
  const queryString = fullPath?.split('?')?.[1]
  if (!queryString) return

  const params = new URLSearchParams(queryString)
  const status = params.get(QueryParamsRating.STATUS)
  const token = params.get(QueryParamsRating.TOKEN)
  const orderId = params.get(QueryParamsRating.ORDER_ID)
  const type = params.get(QueryParamsRating.TYPE)
  const restCode = params.get(QueryParamsRating.REST_CODE) ?? undefined

  if (!token || !orderId || !type) return
  const router = useRouter()
  try {
    router.replace({
      path,
      query: {
        [QueryParamsRating.TOKEN]: undefined,
        [QueryParamsRating.ORDER_ID]: undefined,
        [QueryParamsRating.TYPE]: undefined,
        [QueryParamsRating.STATUS]: undefined,
        [QueryParamsRating.REST_CODE]: undefined,
      },
    })
  } catch (error) {
    debug('🚀 ~ file: app.vue:58 ~ readRatingParams ~ error:', error)
  }
  const statusNumeric = status ? parseInt(status) : undefined
  const ratingIntentData = {
    orderId,
    token,
    type,
    restCode,
    status:
      statusNumeric && Number.isInteger(statusNumeric) && !Number.isNaN(statusNumeric)
        ? statusNumeric
        : undefined,
  }
  appStore.setRatingIntentData(ratingIntentData)
}

const restaurantHomeRoute = (restaurant: RestaurantInfo) => {
  const { code, name, city } = restaurant
  return getRestaurantEcommerceHomeRoute({ code, name, city, t })
}

const readOverrideRestaurantParam = async ({
  path,
  fullPath,
}: {
  path: string
  fullPath: string
}) => {
  const queryString = fullPath?.split('?')?.[1]
  if (!queryString) return
  const QueryParamsOverride = {
    OVERRIDE_RESTAURANT: 'override-restaurant',
  } as const

  const params = new URLSearchParams(queryString)
  const code = params.get(QueryParamsOverride.OVERRIDE_RESTAURANT)
  const overrideRestaurantCode = code ? code.toUpperCase() : undefined
  if (!overrideRestaurantCode) return

  const router = useRouter()
  try {
    router.replace({
      path,
      query: {
        [QueryParamsOverride.OVERRIDE_RESTAURANT]: undefined,
      },
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('🚀 ~ file: app.vue:95 ~ readOverrideRestaurantParam ~ error:', error)
  }

  const restaurantStore = useRestaurantStore()

  const cookieRestaurantCode = restaurantStore.selectedRestaurant?.restaurant?.code || undefined
  if (!cookieRestaurantCode) {
    // no restaurant stored on cookie
    overriding.value = true
    const success = await restaurantStore.autoSelectRestaurant(overrideRestaurantCode)
    if (success)
      await useRouter().push({
        path: restaurantHomeRoute(restaurantStore.selectedRestaurant!.restaurant!),
      })
    overriding.value = false
  } else if (
    cookieRestaurantCode !== overrideRestaurantCode ||
    restaurantStore.areaSelected === RestaurantAreas.DLV
  ) {
    const alertKey = 'overrideRestaurant.autoselectRestaurantAndArea'
    // show alert to confirm process
    const result = await showDialogAlert({
      dialog,
      data: {
        title: t(`${alertKey}.title`),
        body: appStore.cart.products.length ? t(`${alertKey}.body`) : '',
        btnPrimary: t(`${alertKey}.accept`),
        btnSecondary: t(`${alertKey}.cancel`),
      },
    })

    if (result) {
      // autoSelect restaurant
      overriding.value = true
      const success = await restaurantStore.autoSelectRestaurant(overrideRestaurantCode)
      if (success) {
        await navigateTo({
          path: restaurantHomeRoute(restaurantStore.selectedRestaurant!.restaurant!),
        })
        overriding.value = false
      } else {
        overriding.value = false
        const alertKey = 'overrideRestaurant.autoselectRestaurantAndAreaError'
        // show alert to show error in process process
        await showDialogAlert({
          dialog,
          data: {
            title: t(`${alertKey}.title`),
            body: t(`${alertKey}.body`),
            btnPrimary: t(`${alertKey}.accept`),
            btnSecondary: t(`${alertKey}.cancel`),
          },
        })
      }
    }
  }
}

const readRegisterParam = ({ fullPath }: { fullPath: string }) => {
  const queryString = fullPath?.split('?')?.[1]
  const params = new URLSearchParams(queryString)
  const register = params.get(QueryParamsRating.REGISTER)

  if (register === 'true' && screens.value.length !== 0) {
    userHasRegistered.value = true
    showData.value = !!(screens.value && screens.value.length !== 0)
    $toast.show({
      content: t('dataOnboarding.emailValidated'),
      type: ToastType.SUCCESS,
    })
  }
  const router = useRouter()
  try {
    router.replace({
      path,
      query: { [QueryParamsRating.REGISTER]: undefined },
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('🚀 ~ file: app.vue:95 ~ readOverrideRestaurantParam ~ error:', error)
  }
}

nuxtApp.hook('page:start', () => {
  if (process.client) {
    loading.value = true
  }
})

nuxtApp.hook('page:finish', () => {
  loading.value = false
})

watch(error, () => {
  if (error.value) {
    debug('Fetch error initialFetch', error.value)
    errorView.value = error.value
  }
})

watch(initialFetchLoaded, async () => {
  if (initialFetchLoaded.value) {
    if (!appStore.isEcommerceDisabled) {
      await readOverrideRestaurantParam({ path, fullPath })
    }
  }
})

watchEffect(
  () => {
    if (authStore.isUserLogged) {
      readRatingParams({ path: route.path, fullPath: route.fullPath })
    }
  },
  { flush: 'post' }
)

onBeforeMount(async () => {
  screens.value = await useEnrollmentScreens()
  readRegisterParam({ fullPath })
})

useGeolocation()
useDynamicHead()

useHead({
  htmlAttrs: {
    lang: langHtml.value,
  },
})

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'url': `${origin}/`,
  'name': "McDonald's",
  'logo': `${origin}/images/icons/mcDonaldsLogo.svg`,
  'sameas': sameasLinks.value,
}))

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  'name': "McDonald's",
  'url': `${origin}/`,
}))
</script>

<template>
  <LazyErrorView v-if="errorView" :error="errorView" />
  <Site v-else-if="contentStore.site">
    <Loader v-if="loading || overriding" full />
    <NuxtPage />
  </Site>
  <LazyDataOnboarding
    v-if="showData"
    :screens="screens"
    @close-modal="closeShowData"
    @succeded-enroll="succededEnroll"
  />
</template>
